import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { IconButton, Checkbox, FormControlLabel, Box, Typography, Button, Card, CardContent, CardMedia, Chip, Stack, CardActions, Link, Divider, Alert, Badge, Dialog, DialogTitle, DialogContent, FormControl, Select, DialogActions, TextField, InputAdornment, Container, Paper, AppBar, Toolbar, Grid2, Tooltip } from '@mui/material';
import { ThemeProvider, createTheme, alpha } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { DarkMode, LightMode, Info, InfoOutlined, Warning, Language, AccessTime, Search, FilterList, Clear, HelpOutline } from '@mui/icons-material';
import { Analytics } from '@vercel/analytics/react';
import { useTranslation } from 'react-i18next';
import characters from './data/characters.json';
import WelcomeDialog from './components/WelcomeDialog';

const classesList = [...new Set(characters.flatMap(c => c.classes))].sort()


const FilterCharacters = () => {
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(null);
  const [isShowClass, setIsShowClass] = useState(null);
  const [langDialogOpen, setLangDialogOpen] = React.useState(false);
  const [language, setLanguage] = React.useState(null);

  const { t: tCategory, i18n: i18nCategory } = useTranslation('category');
  const { t: tCharacter, i18n: i18nCharacter } = useTranslation('character');

  useEffect(() => {
    const languageValue = localStorage.getItem('language');
    const darkModeValue = JSON.parse(localStorage.getItem('isDarkMode'));
    const showClassValue = JSON.parse(localStorage.getItem('isShowClass'));

    if (languageValue === null) setLanguage("en")
    if (darkModeValue === null) setIsDarkMode(true);
    if (showClassValue === null) setIsShowClass(true);

    if (languageValue !== null) {
      setLanguage(languageValue);
      i18nCategory.changeLanguage(languageValue);
      i18nCharacter.changeLanguage(languageValue);
    }
    if (darkModeValue !== null) setIsDarkMode(darkModeValue);
    if (showClassValue !== null) setIsShowClass(showClassValue);
  }, [i18nCategory, i18nCharacter]);

  useEffect(() => {
    if (isDarkMode !== null) {
      localStorage.setItem('isDarkMode', JSON.stringify(isDarkMode));
    }
  }, [isDarkMode]);

  useEffect(() => {
    if (isShowClass !== null) {
      localStorage.setItem('isShowClass', JSON.stringify(isShowClass));
    }
  }, [isShowClass]);

  useEffect(() => {
    if (language !== null) {
      localStorage.setItem('language', language);
      i18nCategory.changeLanguage(language);
      i18nCharacter.changeLanguage(language);
    }
  }, [language]);


  const handleToggle = () => {
    setIsDarkMode((prevMode) => !prevMode);
  };


  const handleShowClassToggle = () => {
    setIsShowClass((prevMode) => !prevMode);
  };

  const handleLanguageChange = (event) => {
    const language = event.target.value || ''
    setLanguage(language);
  };

  const handleClickLangDialogOpen = () => {
    setLangDialogOpen(true);
  };

  const handleCloseLangDialog = () => {
    setLangDialogOpen(false);
  };

  const theme = createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      primary: {
        main: '#ffab00',
      }, secondary: {
        main: '#ffab00', // Bright amber-like orange
        light: '#ffd740', // Lighter version of the main color
        dark: '#c67c00', // Darker version of the main color
        contrastText: '#212121', // Black text for better contrast
      },
    },
  });

  const handleClassChange = (event) => {
    const { value, checked } = event.target;
    setSelectedClasses((prev) =>
      checked ? [...prev, value] : prev.filter((className) => className !== value)
    );
  };


  const handleClassToggle = useCallback((cls) => {
    setSelectedClasses((prev) => prev.includes(cls)
      ? prev.filter((item) => item !== cls)
      : [...prev, cls]
    );
  }, []);

  const filteredCharacters = useMemo(() =>
    characters.filter((character) => {
      const matchesClasses = selectedClasses?.every((className) => character.classes.includes(className));
      const matchesSearch = character.character.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesClasses && matchesSearch;
    }), [selectedClasses, searchTerm]);

  // Compute which classes are still available in the filtered results
  // This helps users know which classes have matching data
  const availableClasses = useMemo(() =>
    [...new Set(filteredCharacters.flatMap(c => c.classes))],
    [filteredCharacters]);

  return (<ThemeProvider theme={theme}>
    <CssBaseline />
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Modern Header with Glassmorphism */}
      <AppBar position="sticky" sx={{
        bgcolor: alpha(theme.palette.background.paper, 0.25),
        backdropFilter: 'blur(10px)',
        color: 'text.primary',
        boxShadow: 'none',
        borderBottom: `1px solid ${theme.palette.divider}`
      }}>
        <Container maxWidth={false}>
          <Toolbar disableGutters sx={{ justifyContent: 'space-between', flexWrap: 'wrap', py: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: -0.5 }}>
                Let's Party
              </Typography>
            </Stack>

            <Box sx={{ flexGrow: 1, mx: { xs: 0, sm: 4 }, my: { xs: 1, sm: 0 }, minWidth: { xs: '100%', sm: '300px' } }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search character..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchTerm('')}>
                        <Clear fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.divider, 0.05),
                    '& fieldset': { border: 'none' },
                    '&:hover': { bgcolor: alpha(theme.palette.divider, 0.08) }
                  }
                }}
              />
            </Box>

            <Stack direction="row" spacing={0.5} alignItems="center">
              <IconButton onClick={handleToggle} color="inherit">
                {isDarkMode ? <DarkMode /> : <LightMode />}
              </IconButton>
              <IconButton onClick={handleShowClassToggle} color="inherit">
                {isShowClass ? <Info /> : <InfoOutlined />}
              </IconButton>
              <Badge color="secondary" overlap="circular" badgeContent=" " variant="dot">
                <IconButton onClick={handleClickLangDialogOpen} color="inherit">
                  <Language />
                </IconButton>
              </Badge>
              <img
                src="/wip.webp"
                alt="WIP"
                style={{
                  height: 24,
                  width: 'auto',
                  opacity: 0.8,
                  marginLeft: 8,
                  display: 'block'
                }}
              />
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      <Container maxWidth={false} sx={{ py: 4, position: 'relative' }}>
        <WelcomeDialog
          onOpenLangDialog={handleClickLangDialogOpen}
        />


        <Dialog disableEscapeKeyDown open={langDialogOpen} onClose={handleCloseLangDialog}>
          <DialogTitle>Choose your language</DialogTitle>
          <DialogContent>
            <Box component="form" sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: "center" }}>
              <FormControl sx={{ m: 1, minWidth: 120, width: 350 }} >
                <Select
                  native
                  value={language}
                  onChange={handleLanguageChange}
                >
                  <option value={"en"}>English</option>
                  <option value={"ja"}>日本語</option>
                  <option value={"th"}>ไทย</option>
                  <option value={"fr"}>Français</option>
                  <option value={"zh-Hans"}>简体中文</option>
                </Select>
              </FormControl>
            </Box>

            <Grid2 my={2}>
              <Divider id="more-language">
                <Chip label="Special Thanks & More language" size="small" />
              </Divider>
            </Grid2>
            <Grid2 container justifyContent="center">
              <Alert variant="outlined" severity="info">
                Help me by filling up these translations &rarr; <Link href="https://crowdin.com/project/party-animals-helper" target="_blank" variant="caption">Here</Link> 	&larr;
              </Alert>
            </Grid2>
            <Grid2 container justifyContent="center" mt={1} spacing={1}>
              <Chip key={`sp_onigirin`}
                size='small'
                label={"日本語: onigirin"}
                color="secondary"
                variant="outlined"
                clickable
                component="a"
                href="https://www.reddit.com/user/onigirin" />
              <Chip key={`sp_usuallyduck`}
                size='small'
                label={"ไทย: usuallyduck"}
                color="secondary"
                variant="outlined"
                clickable
                component="a"
                href="https://crowdin.com/profile/usuallyduck" />
              <Chip key={`sp_vegafr`}
                size='small'
                label={"Français:  Vega-FR"}
                color="secondary"
                variant="outlined"
                clickable
                component="a"
                href="https://crowdin.com/profile/Vega-FR" />
              <Chip key={`sp_vegafr`}
                size='small'
                label={"简体中文:  Ews"}
                color="secondary"
                variant="outlined"
                clickable
                component="a"
                href="https://crowdin.com/profile/ewa224336" />
            </Grid2>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseLangDialog}>Ok</Button>
          </DialogActions>
        </Dialog>
        <Paper elevation={0} sx={{ p: 3, borderRadius: 4, mb: 4, bgcolor: alpha(theme.palette.background.paper, 0.4), backdropFilter: 'blur(10px)', border: `1px solid ${theme.palette.divider}` }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <FilterList fontSize="small" color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Filters</Typography>
            <Chip size="small" label={`${filteredCharacters.length} / ${characters.length}`} color="primary" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.75rem', height: 22 }} />
          </Stack>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {classesList.map((className) => {
              const isSelected = selectedClasses.includes(className);
              const isAvailable = availableClasses.includes(className);
              // Show dashed border when: class is not selected, but has matching members in filtered results
              const showDashedHint = !isSelected && isAvailable && selectedClasses.length > 0;

              return (
                <Chip
                  key={className}
                  label={tCategory(className)}
                  onClick={() => handleClassToggle(className)}
                  color={isSelected ? "primary" : "default"}
                  variant={isSelected ? "filled" : "outlined"}
                  sx={{
                    borderRadius: '8px',
                    fontWeight: isSelected ? 600 : 400,
                    '&:hover': { transform: 'translateY(-2px)' },
                    ...(showDashedHint && {
                      borderStyle: 'dashed',
                      opacity: 0.85,
                    }),
                    ...(!isAvailable && selectedClasses.length > 0 && !isSelected && {
                      opacity: 0.4,
                    }),
                  }}
                />
              );
            })}
          </Box>
          <Button
            size="small"
            variant="text"
            color="primary"
            onClick={() => setSelectedClasses([])}
            disabled={!selectedClasses.length}
            startIcon={<Clear />}
            sx={{ borderRadius: 2 }}
          >
            {tCategory('Clear Filters')}
          </Button>
        </Paper>

        <Grid2 container spacing={1} justifyContent="flex-start">
          {filteredCharacters.length === 0 ? (
            <Box sx={{ py: 10, textAlign: 'center', width: '100%' }}>
              <Typography variant="h6" color="text.secondary">No characters match your search or filters.</Typography>
              <Button size="small" onClick={() => { setSelectedClasses([]); setSearchTerm(''); }} sx={{ mt: 1 }}>Clear Filters</Button>
            </Box>
          ) : (
            filteredCharacters.map((character) => (
              <Grid2 size={{ xs: 4, sm: 3, md: 2, lg: 1.5, xl: 1 }} key={character.character} sx={{ display: 'flex' }}>
                <Card sx={{
                  flexGrow: 1,
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 4,
                  width: '100%',
                  overflow: 'hidden',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  bgcolor: character.unconfirmed ? alpha(theme.palette.warning.main, 0.05) : alpha(theme.palette.background.paper, 0.6),
                  backdropFilter: 'blur(10px)',
                  border: character.unconfirmed
                    ? `1px dashed ${alpha(theme.palette.warning.main, 0.5)}`
                    : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: (theme) => `0 12px 24px ${alpha(theme.palette.common.black, 0.15)}`,
                    borderColor: 'primary.main',
                  }
                }}>
                  <Box sx={{ position: 'relative', pt: '100%', overflow: 'hidden' }}>
                    <CardMedia
                      component="img"
                      image={character.imageUrl.split('/revision')[0]}
                      alt={character.character}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.5s ease',
                        '&:hover': { transform: 'scale(1.1)' }
                      }}
                    />
                  </Box>
                  <CardContent sx={{ flexGrow: 1, p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: 1,
                        lineHeight: 1.2,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {tCharacter(character.character)}
                    </Typography>
                    {isShowClass && (
                      <Stack
                        direction="row"
                        flexWrap="wrap"
                        gap={0.5}
                        sx={{
                          minHeight: '52px', // Minimum height for 2 rows of chips
                          alignContent: 'flex-start'
                        }}
                      >
                        {character.classes.map((cls, index) => (
                          <Chip
                            key={`${character.character}_${index}`}
                            size="small"
                            label={tCategory(cls)}
                            color={selectedClasses.includes(cls) ? "primary" : "default"}
                            variant={selectedClasses.includes(cls) ? "filled" : "outlined"}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClassToggle(cls);
                            }}
                            sx={{
                              fontSize: '0.7rem',
                              height: 22,
                              borderRadius: '6px'
                            }}
                          />
                        ))}
                      </Stack>
                    )}
                    {(character.unconfirmed || character.comingsoon) && (
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{ mt: 2 }}>
                        {character.comingsoon && (
                          <Chip
                            size="small"
                            label="Soon"
                            color="success"
                            sx={{ fontWeight: 700, height: 18, fontSize: '0.6rem', borderRadius: 1 }}
                          />
                        )}
                        {character.unconfirmed && (
                          <Tooltip title="This character's data is not yet confirmed" arrow>
                            <Chip
                              size="small"
                              label="Unconfirmed"
                              color="warning"
                              variant="outlined"
                              icon={<HelpOutline sx={{ fontSize: '0.7rem !important' }} />}
                              sx={{ fontWeight: 700, height: 18, fontSize: '0.6rem', borderRadius: 1 }}
                            />
                          </Tooltip>
                        )}
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              </Grid2>
            ))
          )}
        </Grid2>

        <Box sx={{ mt: 8, pb: 4, textAlign: 'center' }}>
          <Divider sx={{ mb: 3 }} />
          <Stack spacing={1} alignItems="center">
            <Link href="https://party-animals.fandom.com" target="_blank" variant="caption" color="text.secondary">Source: Party Animals Wiki</Link>
            <Link href="https://discord.gg/partyanimals" target="_blank" variant="caption" color="text.secondary">Party Animals Discord</Link>
          </Stack>
        </Box>
      </Container>
    </Box>
    <Analytics />
  </ThemeProvider >
  );
};


export default FilterCharacters;